import { z } from 'zod';
import { findingGroups } from '@/features/loop-intelligence/components/data/types';

/**
 * Which loop's run is being read, as the URL states it: absent means the loop navigated to, `'all'`
 * means every readable loop merged, and a number means that loop specifically. Never stored as a bare
 * `number | null` in the URL — an absent param has to mean "the nav loop", not "all loops", so the two
 * need distinct spellings. `loopScope.ts` turns this into the `number | null` the rest of the page
 * reasons about.
 */
export const loopParamSchema = z
  .union([z.literal('all'), z.coerce.number().int().positive()])
  .optional()
  .catch(undefined);

/**
 * The whole surface's state lives in the URL, so a link reproduces exactly what someone was looking
 * at — which run, which loop, filtered to which group and objective, with which finding open.
 *
 * No reading to address, unlike the briefing line: a feed has one shape, and the rail-and-pane mode
 * has no place in it.
 *
 * Every field is `.catch(undefined)` rather than plain `.optional()`: these arrive from a
 * hand-editable address bar, and a value that does not resolve should fall back to the default rather
 * than throw a route error at someone who mistyped a link.
 */
export const loopIntelligenceSearchSchema = z.object({
  objective: z.string().min(1).optional().catch(undefined),
  group: z.enum(findingGroups).optional().catch(undefined),
  finding: z.string().min(1).optional().catch(undefined),
  /**
   * Set only when a reader closed the open card. Without a flag of its own, "nothing named yet"
   * and "deliberately closed" are the same state (`finding: undefined`) and a naive close would
   * immediately re-open the first card — this is what tells the feed the two apart, and it is a
   * separate field rather than a sentinel `finding` value so a closed feed still survives a reload.
   */
  closed: z.literal(true).optional().catch(undefined),
  loop: loopParamSchema,
});

export type LoopIntelligenceSearch = z.infer<typeof loopIntelligenceSearchSchema>;
